/**
 * Pick-n-Mix Machine state controller.
 * Bags are built client-side; nothing touches the real Shopify cart until
 * "Seal it" is pressed (Phase 2). The 8 oz minimum applies ONLY to bag #1
 * of the whole order: once a first bag with >=8 oz exists (either already
 * sealed into the cart, or included in the current seal), every other bag
 * — sealed now or added later — has no minimum at all, even 1 oz.
 * cartBagCount (how many bags already exist in the live cart, from
 * distinct _bag_number properties) is what tells canSeal() whether bag #1
 * is already satisfied.
 */

const BAG_MAX_OZ = 15;
const BAG_MIN_OZ = 8;
const HALLOWEEN_MIN_OZ = 12;
const PIECES_PER_OZ = 2;
// Bulky candies (tag `bulky`: fluffy banana, marshmallow, sugar cubes, Ahlgrens...)
// take almost twice the room in a bag: 1 oz of them uses 15/8 oz of the 15 oz bag, so
// exactly 8 oz of bulky candy fills a bag (which is also the 8 oz minimum, so a
// bag of only marshmallows can be sealed). Price and the minimum still use real oz.
// 15/8 = 1.875 is exact in binary, so the sums below never drift.
const BULKY_SPACE = 15 / 8;

function uid() {
  return 'bag_' + Math.random().toString(36).slice(2, 10);
}

class MixBuilderState extends EventTarget {
  constructor(candies) {
    super();
    // candies: [{ id, name, category, dietTags: [], pricePerOz, inStock }]
    // each candy also carries mode: 'classic' | 'halloween'
    this.candies = candies;
    this.candyById = Object.fromEntries(candies.map((c) => [c.id, c]));
    // The first bag of the whole order must reach the minimum for ITS mode
    // (Classic 8 oz, Halloween 12 oz by default); every other bag has none.
    this.minByMode = { classic: BAG_MIN_OZ, halloween: HALLOWEEN_MIN_OZ };
    this.bags = [{ id: uid(), mode: 'classic', items: {} }]; // items: { candyId: oz }
    this.activeBagIndex = 0;
    this.cartOzTotal = 0; // set by Phase 2 cart sync; 0 until then
    this.cartBagCount = 0; // distinct bags already in the live cart; 0 until synced
    this.cartBagMax = 0; // highest bag number already in the live cart (numbers can have gaps once a bag is removed)
    this.cartBags = []; // sealed bags in the live cart: { number, id, mode, items, lineKeys, oz, price, canReopen }
  }

  get activeBag() {
    return this.bags[this.activeBagIndex];
  }

  get mode() {
    return this.activeBag.mode || 'classic';
  }

  // The first bag of each mix type in an order has that type's minimum (classic 8 oz, Halloween 12 oz). Other bags of the
  // same type have none. A type is "covered" once the cart already holds a bag of it; for classic any bag in the cart counts.
  minOz(bagIndex = this.activeBagIndex) {
    const bag = this.bags[bagIndex] || this.bags[0];
    return this.minByMode[bag.mode || 'classic'] || BAG_MIN_OZ;
  }

  firstBagOfMode(mode) {
    return this.bags.findIndex((b) => (b.mode || 'classic') === mode);
  }

  modeCovered(mode) {
    if (mode === 'halloween') return (this.cartBags || []).some((b) => b.mode === 'halloween');
    return this.cartBagCount >= 1;
  }

  // oz this bag must reach before the order can be sealed; 0 when it has no minimum
  requirementFor(bagIndex) {
    const bag = this.bags[bagIndex];
    if (!bag) return 0;
    const mode = bag.mode || 'classic';
    if (this.modeCovered(mode) || this.firstBagOfMode(mode) !== bagIndex) return 0;
    return this.minOz(bagIndex);
  }

  ozNeededForBag(bagIndex) {
    const req = this.requirementFor(bagIndex);
    return req ? Math.max(0, req - this.bagWeight(bagIndex)) : 0;
  }

  // first bag with candy in it that is still under its minimum, or null
  unmetRequirement() {
    for (let i = 0; i < this.bags.length; i++) {
      const w = this.bagWeight(i);
      const req = this.requirementFor(i);
      if (w > 0 && req > 0 && w < req) return { index: i, mode: this.bags[i].mode || 'classic', min: req, need: req - w };
    }
    return null;
  }

  setMode(mode) {
    if (mode !== 'classic' && mode !== 'halloween') return;
    if (this.mode === mode) return;
    const active = this.activeBag;
    if (this.bagWeight(this.activeBagIndex) === 0) {
      active.mode = mode;
    } else {
      let idx = -1;
      this.bags.forEach((b, i) => { if ((b.mode || 'classic') === mode) idx = i; });
      if (idx === -1) {
        this.bags.push({ id: uid(), mode, items: {} });
        idx = this.bags.length - 1;
      }
      this.activeBagIndex = idx;
    }
    this._emitChange();
  }

  bagWeight(bagIndex) {
    const bag = this.bags[bagIndex];
    return Object.values(bag.items).reduce((sum, oz) => sum + oz, 0);
  }

  // how much of the 15 oz bag is used up (bulky candy counts double)
  spaceOf(candyId) {
    const candy = this.candyById[candyId];
    return candy && candy.bulky ? BULKY_SPACE : 1;
  }

  bagSpace(bagIndex) {
    const bag = this.bags[bagIndex];
    return Object.entries(bag.items).reduce((sum, [candyId, oz]) => sum + oz * this.spaceOf(candyId), 0);
  }

  bagPrice(bagIndex) {
    const bag = this.bags[bagIndex];
    return Object.entries(bag.items).reduce((sum, [candyId, oz]) => {
      const candy = this.candyById[candyId];
      return sum + (candy ? candy.pricePerOz * oz : 0);
    }, 0);
  }

  orderTotalOz() {
    return this.bags.reduce((sum, _, i) => sum + this.bagWeight(i), 0);
  }

  orderTotalPrice() {
    return this.bags.reduce((sum, _, i) => sum + this.bagPrice(i), 0);
  }

  canSeal() {
    const anyCandy = this.bags.some((_, i) => this.bagWeight(i) > 0);
    // nothing in the machine: only already-secured orders may press seal (it then does nothing)
    if (!anyCandy) return this.cartBagCount >= 1;
    return this.unmetRequirement() === null;
  }

  // oz still needed before sealing unlocks, for the bag that is blocking it (0 once everything meets its minimum)
  ozNeededForFirstBag() {
    const unmet = this.unmetRequirement();
    if (unmet) return unmet.need;
    if (!this.bags.some((_, i) => this.bagWeight(i) > 0) && this.cartBagCount < 1) return this.minOz(0);
    return 0;
  }

  addOz(candyId) {
    const candy = this.candyById[candyId];
    if (!candy || !candy.inStock) return;
    if ((candy.mode || 'classic') !== this.mode) return;

    let bag = this.activeBag;
    if (this.bagSpace(this.activeBagIndex) + this.spaceOf(candyId) > BAG_MAX_OZ) {
      // the bag is full: go on in an empty bag that already exists, otherwise start a new one
      const spare = this.bags.findIndex((b, i) => i !== this.activeBagIndex && (b.mode || 'classic') === this.mode && this.bagWeight(i) === 0);
      if (spare > -1) {
        bag = this.bags[spare];
        this.activeBagIndex = spare;
      } else {
        bag = { id: uid(), mode: this.mode, items: {} };
        this.bags.push(bag);
        this.activeBagIndex = this.bags.length - 1;
      }
    }

    bag.items[candyId] = (bag.items[candyId] || 0) + 1;
    this._emitChange();
  }

  removeOz(candyId, bagIndex = this.activeBagIndex) {
    const bag = this.bags[bagIndex];
    if (!bag || !bag.items[candyId]) return;

    bag.items[candyId] -= 1;
    if (bag.items[candyId] <= 0) delete bag.items[candyId];
    this._emitChange();
  }

  moveItem(candyId, fromBagIndex, toBagIndex) {
    if (fromBagIndex === toBagIndex) return;
    const fromBag = this.bags[fromBagIndex];
    const toBag = this.bags[toBagIndex];
    if (!fromBag || !toBag || !fromBag.items[candyId]) return;

    // Only as many oz as still fit in the destination bag (bulky counts double);
    // anything that does not fit stays where it was.
    const per = this.spaceOf(candyId);
    const room = Math.floor((BAG_MAX_OZ - this.bagSpace(toBagIndex)) / per);
    const oz = Math.min(fromBag.items[candyId], room);
    if (oz <= 0) {
      this.dispatchEvent(new CustomEvent('bagfull', { detail: { bagIndex: toBagIndex } }));
      return;
    }
    fromBag.items[candyId] -= oz;
    if (fromBag.items[candyId] <= 0) delete fromBag.items[candyId];
    toBag.items[candyId] = (toBag.items[candyId] || 0) + oz;
    this._emitChange();
  }

  // Global bag number of a bag in this session: a reopened bag keeps its own number, new bags continue
  // after the highest number in use (cart + reopened), so two bags can never share a number.
  bagNumber(bagIndex) {
    const bag = this.bags[bagIndex];
    if (bag && bag.number) return bag.number;
    let top = this.cartBagMax;
    this.bags.forEach((b) => { if (b.number && b.number > top) top = b.number; });
    let n = top;
    for (let j = 0; j <= bagIndex; j++) { if (!this.bags[j].number) n += 1; }
    return n;
  }

  // Put a sealed bag from the cart back into the machine so it can be looked at and added to.
  // It stays in the cart until it is sealed again, so leaving the page loses nothing.
  loadSealedBag(sealed) {
    const bag = { id: sealed.id, mode: sealed.mode || 'classic', items: { ...sealed.items }, number: sealed.number, name: sealed.name || '', replacesKeys: sealed.lineKeys };
    const only = this.bags.length === 1 && Object.keys(this.bags[0].items).length === 0 && !this.bags[0].number;
    if (only) { this.bags[0] = bag; this.activeBagIndex = 0; } else { this.bags.push(bag); this.activeBagIndex = this.bags.length - 1; }
    this._emitChange();
  }

  selectBag(bagIndex) {
    if (bagIndex < 0 || bagIndex >= this.bags.length) return;
    this.activeBagIndex = bagIndex;
    this._emitChange();
  }

  deleteBag(bagIndex) {
    if (this.bags.length <= 1) {
      this.bags[0].items = {};
    } else {
      this.bags.splice(bagIndex, 1);
      if (this.activeBagIndex >= this.bags.length) {
        this.activeBagIndex = this.bags.length - 1;
      }
    }
    this._emitChange();
  }

  // Start over: one empty bag, nothing open for editing (cart lines are removed separately by the UI)
  resetAllBags() {
    this.bags = [{ id: uid(), mode: this.mode, items: {} }];
    this.activeBagIndex = 0;
    this._emitChange();
  }

  emptyBag(bagIndex = this.activeBagIndex) {
    this.bags[bagIndex].items = {};
    this._emitChange();
  }

  startNewBag() {
    this.bags.push({ id: uid(), mode: this.mode, items: {} });
    this.activeBagIndex = this.bags.length - 1;
    this._emitChange();
  }

  _emitChange() {
    this._persist();
    this.dispatchEvent(new CustomEvent('change'));
  }

  _persist() {
    try {
      sessionStorage.setItem(
        'pym-bags',
        JSON.stringify({ bags: this.bags, activeBagIndex: this.activeBagIndex })
      );
    } catch (e) {
      /* storage unavailable — builder still works, just won't survive a refresh */
    }
  }

  restore() {
    try {
      const raw = sessionStorage.getItem('pym-bags');
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && Array.isArray(saved.bags) && saved.bags.length) {
        this.bags = saved.bags.map((b) => ({ ...b, mode: b.mode || 'classic' }));
        this.activeBagIndex = saved.activeBagIndex || 0;
      }
    } catch (e) {
      /* corrupt/blocked storage — start fresh */
    }
  }
}

window.MixBuilderState = MixBuilderState;
window.MIX_BUILDER_CONSTANTS = { BAG_MAX_OZ, BAG_MIN_OZ, HALLOWEEN_MIN_OZ, PIECES_PER_OZ, BULKY_SPACE };
